package top.wcpe.allincore.ui.runtime.element

import kotlin.jvm.JvmStatic

/**
 * 自动滚动装饰器：每帧把 body.scrollY 按 [speed]（像素/秒）匀速推进。常见于歌词/弹幕/赛后报告滚动。
 *
 * [tickFrame] 按相邻帧时间差 dt 推进，与帧率无关。[paused]=true 或 [pauseOnHover]=true 且 body 处于
 * hover 时暂停。[loopBack]=true 时滚到底自动跳回顶部，形成循环跑马灯。
 */
class AutoScroll private constructor() :
    ScrollDecorator<AutoScroll>(),
    FrameTickable {

    private var speedValue: Float = DEFAULT_SPEED
    private var pausedFlag: Boolean = false
    private var pauseOnHoverFlag: Boolean = true
    private var loopBackFlag: Boolean = false

    // 上一帧时刻，用于按 dt 推进；-1 表示尚未开始
    private var lastTickMs: Long = -1L

    protected override fun self(): AutoScroll = this

    // -------- 字段 --------

    fun speed(pxPerSec: Float): AutoScroll {
        speedValue = pxPerSec
        return this
    }

    fun speed(): Float = speedValue

    fun paused(p: Boolean): AutoScroll {
        pausedFlag = p
        return this
    }

    fun paused(): Boolean = pausedFlag

    fun pauseOnHover(p: Boolean): AutoScroll {
        pauseOnHoverFlag = p
        return this
    }

    fun pauseOnHover(): Boolean = pauseOnHoverFlag

    fun loopBack(l: Boolean): AutoScroll {
        loopBackFlag = l
        return this
    }

    fun loopBack(): Boolean = loopBackFlag

    // -------- 帧推进 --------

    /** 当前是否应推进（未暂停、未 hover 暂停、body 存在）。 */
    fun active(): Boolean {
        val b = bodyField ?: return false
        if (pausedFlag) {
            return false
        }
        if (pauseOnHoverFlag && (hovered() || b.hovered())) {
            return false
        }
        return true
    }

    override fun tickFrame(nowMs: Long): Boolean {
        if (lastTickMs < 0L) {
            lastTickMs = nowMs
            return false
        }
        val dtSec = maxOf(0f, (nowMs - lastTickMs).toFloat() / MS_PER_SECOND)
        lastTickMs = nowMs
        if (!active() || dtSec <= 0f) {
            return false
        }
        advance(dtSec)
        return true
    }

    /**
     * 按 dt 推进一次 scrollY。暴露为 public 便于测试直接驱动（不依赖帧时钟）。
     *
     * @param dtSec 时间增量（秒）
     */
    fun advance(dtSec: Float) {
        val b = bodyField ?: return
        val before = b.scrollY()
        var next = before + speedValue * dtSec
        // loopBack：正向滚到底则回到顶部；反向滚到顶则跳到底部
        if (loopBackFlag) {
            val max = b.maxScrollY()
            if (max > 0f) {
                if (next >= max && speedValue > 0f && before >= max - LOOP_EPSILON) {
                    next = 0f
                } else if (next <= 0f && speedValue < 0f && before <= LOOP_EPSILON) {
                    next = max
                }
            }
        }
        b.scrollY(next)
    }

    companion object {
        /** 默认滚动速度（像素/秒）。 */
        private const val DEFAULT_SPEED = 20f

        /** 每秒毫秒数，用于把帧间隔换算成秒。 */
        private const val MS_PER_SECOND = 1000f

        /** loopBack 回绕的边界容差（像素）：贴近边界才触发回绕，避免抖动。 */
        private const val LOOP_EPSILON = 0.5f

        /** 创建一个自动滚动装饰器。 */
        @JvmStatic
        fun create(): AutoScroll = AutoScroll()
    }
}
